import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { collectTracking } from "@/lib/utm";
import LeadFields from "@/components/LeadFields";
import IntelligenceCard from "@/components/IntelligenceCard";
import { RISK_WATCH_SOURCES } from "@/lib/constants";
import { ArrowRight, Newspaper } from "lucide-react";

export default function RiskWatch() {
  const [values, setValues] = useState({
    first_name: "", last_name: "", email: "", mobile: "",
    company: "", buyer_type: "", role: "", consent: false,
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [intel, setIntel] = useState({ loading: true, items: [] });

  useEffect(() => {
    api.get("/intelligence")
      .then(({ data }) => setIntel({ loading: false, items: data.items || [] }))
      .catch(() => setIntel({ loading: false, items: [] }));
  }, []);

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) return toast.error("Please tick the consent box to continue.");
    setSubmitting(true);
    try {
      await api.post("/leads/risk-watch", {
        ...values,
        tracking: collectTracking({ formType: "risk_watch", enquiryType: "pfas_src_risk_watch" }),
      });
      setDone(true);
      toast.success("You are subscribed to PFAS Risk Watch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="kicker mb-4">Free · Curated · Editorially reviewed</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight" data-testid="risk-watch-title">
            A curated UK PFAS land-risk intelligence service.
          </h1>
          <p className="mt-6 text-white/65 leading-relaxed">
            <span className="text-white">PFAS Risk Watch</span> tracks recent PFAS developments from credible UK sources and explains what changed, why it matters, who may be affected and what it could mean for planning, acquisition, development, lending, insurance, remediation or legal strategy.
          </p>
          <div className="mt-8">
            <div className="kicker mb-3">Sources we track</div>
            <div className="flex flex-wrap gap-2">
              {RISK_WATCH_SOURCES.map((s) => (
                <span key={s} className="border border-white/15 px-2.5 py-1 text-[10px] font-mono-alt tracking-[0.12em] uppercase text-white/65">{s}</span>
              ))}
            </div>
          </div>
          <p className="mt-8 text-xs text-white/40 leading-relaxed">
            Every item is researched from an authoritative source, reviewed by us, summarised in our own words and published deliberately with a link to the original. No automated feeds, no republished third-party articles.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="border border-white/10 bg-[#0C0E13] p-6 md:p-10">
            {!done ? (
              <form onSubmit={submit} className="space-y-8">
                <LeadFields values={values} onChange={setField} showRole
                  consentLabel="I agree to receive PFAS Risk Watch and relevant PFAS land-risk information from PFAS Risk Intelligence."
                  idPrefix="risk-watch" />
                <button type="submit" disabled={submitting} data-testid="risk-watch-submit-btn"
                  className="inline-flex items-center gap-2 bg-white text-[#0A0B0E] px-6 py-3.5 text-[12px] font-mono-alt tracking-[0.18em] uppercase hover-lift hover:bg-white/90 disabled:opacity-40">
                  {submitting ? "Subscribing…" : "Subscribe to Risk Watch"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                </button>
              </form>
            ) : (
              <div className="space-y-6" data-testid="risk-watch-success">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
                    <Newspaper className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="kicker text-emerald-400">Subscribed</div>
                </div>
                <h2 className="font-display text-3xl leading-tight">You are subscribed to PFAS Risk Watch.</h2>
                <p className="text-white/65">
                  You will receive curated PFAS land-risk intelligence covering regulation, investigations, planning decisions, enforcement, insurance and legal developments.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="mt-24 border-t border-white/[0.08] pt-16" id="developments">
        <div className="kicker mb-4">Latest PFAS intelligence</div>
        <h2 className="font-display text-3xl md:text-4xl leading-tight" data-testid="developments-title">Recent PFAS developments</h2>
        {intel.loading ? (
          <div className="mt-10 text-sm text-white/40">Loading…</div>
        ) : intel.items.length === 0 ? (
          <div className="mt-10 border border-white/10 bg-[#0C0E13] p-8 md:p-10" data-testid="developments-empty">
            <div className="font-display text-2xl">First PFAS Risk Watch briefing coming soon.</div>
            <p className="mt-3 text-sm text-white/55 max-w-2xl leading-relaxed">
              Subscribe above to receive it when published. Each briefing will carry the publication date, source organisation, a link to the original source and our own concise commentary.
            </p>
          </div>
        ) : (
          <div className="mt-10 space-y-6" data-testid="developments-list">
            {intel.items.map((it) => <IntelligenceCard key={it.item_id} item={it} />)}
          </div>
        )}
      </section>
    </div>
  );
}
